import {useEffect,useRef,useState} from 'react';
import {useLanguage} from './i18n/LanguageContext.js';
import {checkoutCopy} from './checkout-copy.js';
import * as api from './commerce-api.js';
import {paymentCopy,closedPaymentCopy,verifiedPaymentUrl} from './order-payment.js';
import './order-payment.css';

export function OrderPaymentStatus({order}){
 const {language}=useLanguage(),copy=paymentCopy[language]||paymentCopy.en;
 return <span translate="no">{order.status==='payment_reversed'?(closedPaymentCopy[language]||closedPaymentCopy.en):(copy[order.status]||copy.pending_quote)}</span>;
}

export function CheckoutPaymentHint(){
 const {language}=useLanguage();
 const [enabled,setEnabled]=useState(false);
 useEffect(()=>{let disposed=false;api.getStoreConfiguration().then(data=>{if(!disposed)setEnabled(Boolean(data.paymentEnabled));}).catch(()=>{});return()=>{disposed=true;};},[]);
 const hint={en:'After submitting, open your order to confirm the final quote and pay.',zh:'提交后，请在订单详情确认正式报价并付款。',fr:'Après l’envoi, ouvrez votre commande pour confirmer le devis et payer.',de:'Öffnen Sie nach dem Absenden Ihre Bestellung, um das Angebot zu bestätigen und zu bezahlen.',es:'Después de enviar el pedido, ábrelo para confirmar el presupuesto y pagar.'};
 return enabled?<p className="commerce-muted" translate="no">{hint[language]||hint.en}</p>:<button className="sf-button sf-button-secondary" disabled>正式付款尚未开放</button>;
}

export default function OrderPayment({order,onUpdated}){
 const {language}=useLanguage(),copy=paymentCopy[language]||paymentCopy.en;
 const [accepted,setAccepted]=useState(false),[provider,setProvider]=useState(''),[busy,setBusy]=useState(false),[error,setError]=useState('');
 const direct=order.checkoutMode==='direct',checkoutText=checkoutCopy[language]||checkoutCopy.en;
 const busyRef=useRef(false),updatedRef=useRef(onUpdated);updatedRef.current=onUpdated;
 const quote=order.finalQuote,payment=order.payment,methods=order.paymentMethods||[];
 const paid=['paid','partially_refunded','refunded','reversed'].includes(order.payment_status),review=order.payment_status==='review';
 const active=payment&&['creating','pending'].includes(payment.status);
 const selected=active?payment.provider:methods.includes(provider)?provider:methods[0]||'';
 const format=value=>new Intl.NumberFormat(language,{style:'currency',currency:'USD'}).format(Number(value));
 useEffect(()=>{setAccepted(false);setError('');},[order.number,quote?.id]);
 useEffect(()=>{
  const params=new URLSearchParams(window.location.search);
  if(!payment?.id||params.get('payment_return')!==payment.id||!active)return;
  let disposed=false,timer,attempts=0;
  async function refresh(){
   if(busyRef.current){timer=setTimeout(refresh,3000);return;}
   busyRef.current=true;setBusy(true);
   try{
    const next=await api.confirmOrderPayment(order.number);
    if(disposed)return;
    updatedRef.current(next);
    if(['creating','pending'].includes(next.payment?.status)&&++attempts<6)timer=setTimeout(refresh,5000);
   }catch{if(!disposed)setError(copy.error);}
   finally{busyRef.current=false;if(!disposed)setBusy(false);}
  }
  refresh();return()=>{disposed=true;clearTimeout(timer);};
 },[order.number,payment?.id,Boolean(active),copy.error]);
 async function verify(){
  if(busyRef.current)return;busyRef.current=true;setBusy(true);setError('');
  try{onUpdated(await api.confirmOrderPayment(order.number));}catch{setError(copy.error);}
  finally{busyRef.current=false;setBusy(false);}
 }
 async function pay(event){
  event.preventDefault();if(!accepted||busyRef.current)return;
  busyRef.current=true;setBusy(true);setError('');
  try{
   const result=await api.startOrderPayment(order.number,quote.id,selected);
   window.location.assign(verifiedPaymentUrl(result.redirectUrl,selected,order.paymentEnvironment));
  }catch(e){setError(e.status===409?copy.changed:copy.error);}
  finally{busyRef.current=false;setBusy(false);}
 }
 if(!quote)return null;
 const expired=quote.expired||Date.parse(quote.expiresAt)<=Date.now();
 const result=order.payment_status==='reversed'?(closedPaymentCopy[language]||closedPaymentCopy.en):order.payment_status==='paid'?copy.received:copy[order.payment_status];
 return <section className="order-payment" aria-label={paid?result:copy.title} translate="no">
  <h3 role={paid?'status':undefined} translate="no">{paid?result:direct?checkoutText.summary:copy.title}</h3>
  {order.paymentEnvironment==='test'&&<p className="order-payment-test" translate="no">{copy.test}</p>}
  <dl>{['subtotal','shipping','tax','total'].map(key=><div key={key}><dt translate="no">{direct?checkoutText[key]:copy[key]}</dt><dd translate="no">{quote.taxTreatment==='included'&&key==='tax'?checkoutText.included:format(quote[key])}</dd></div>)}</dl>
  {!paid&&!review&&<>
   {direct&&<p className="order-payment-terms" translate="no">{checkoutText.inclusive}</p>}
   <p className="order-payment-terms" translate="no">{quote.terms}</p>
   <p translate="no">{copy.expires} {new Date(quote.expiresAt).toLocaleString(language)}</p>
  </>}
  {paid?null:review?<p role="status" translate="no">{copy.review}</p>:<>
   {new URLSearchParams(window.location.search).has('payment_cancelled')&&<p role="status" translate="no">{copy.cancel}</p>}
   {active&&<p role="status" translate="no">{copy.pending}</p>}
   {!order.paymentEnabled?<p role="status" translate="no">{copy.unavailable}</p>:expired&&!active?<p role="status" translate="no">{copy.expired}</p>:<form onSubmit={pay}>
    <label className="commerce-field"><span translate="no">{copy.choose}</span><select aria-label={copy.choose} value={selected} disabled={busy||Boolean(active)} onChange={e=>setProvider(e.target.value)}>{methods.map(method=><option key={method} value={method} translate="no">{method==='stripe'?copy.card:'PayPal'}</option>)}</select></label>
    <label className="commerce-checkbox"><input type="checkbox" checked={accepted} onChange={e=>setAccepted(e.target.checked)} required/><span translate="no">{copy.agree}</span></label>
    <button className="sf-button" disabled={busy||!accepted||!selected} translate="no">{busy?copy.busy:copy.pay}</button>
   </form>}
  </>}
  {Number(payment?.refunded)>0&&<p translate="no">{copy.refund}: {format(payment.refunded)}</p>}
  {paid&&<a className="sf-button order-payment-orders" href="/account/orders" translate="no">{copy.viewOrders}</a>}
  {payment&&!paid&&<button className="sf-text-button" disabled={busy} onClick={verify} translate="no">{busy?copy.busy:copy.verify}</button>}
  {error&&!paid&&<p role="alert" className="commerce-notice error" translate="no">{error}</p>}
 </section>;
}
